import { apiErrorMessage } from '@agos/api-client'
import {
  getGetOrderTabsAllQueryKey,
  getGetOrderTabsQueryKey,
  useGetOrderTabsAll,
  useGetSalesChannels,
  useGetOrders,
  usePostOrderTabs,
  usePostOrderTabsIdOrdersOrderId,
  usePostOrderTabsIdSettle,
} from '@agos/api-client/orders'
import {
  allowsTab,
  filterChannelsOf,
  formatCents,
  formatCount,
  formatDateTime,
  T,
  toDateParam,
  tt,
  useModalFocus,
} from '@agos/shared'
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  cn,
  EmptyState,
  PageHeader,
  Spinner,
  StatCard,
  StatusBadge,
  TabGroup,
  useToast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Banknote, Clock, Plus, Receipt, Wallet, X } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'

import {
  type OpenForm,
  openTabProblems,
  openProblemMessage,
  openPayload,
  openFormFor,
  canOpen,
  PAYMENT_METHODS,
  candidatesOf,
  eligibleForTab,
  noCandidatesNote,
  ordersOnAnyTab,
  ageLabel,
  canSettle,
  daysOpen,
  featureNote,
  isOpen,
  isStale,
  oldestFirst,
  outstandingNote,
  paymentLabel,
  settleFormFor,
  settlePayload,
  settleProblemMessage,
  settleProblems,
  settleSummary,
  tabsOf,
  totalsOf,
  wouldClose,
  type CandidateOrder,
  type SettleForm,
  type Tab,
  noDocumentYetNote,
} from './order-tabs'

/**
 * `/order-tabs` — §9.5's *na sekeru* screen. **A justified EXTRA** in the parity diff, citing that section.
 *
 * The two things this screen exists to say, both from `OrderTabService`'s own docblock:
 *
 * - **An open tab is not missing cash.** It is excluded from what a waiter hands over, on purpose, because
 *   the sale has not been registered yet.
 * - **Nothing is fiscalised until settlement**, and that is the FiskalPRO protocol rather than a shortcut:
 *   there is no unpaid document, so the receipt is written for the amount that arrived and the method it
 *   arrived by.
 *
 * **FB-182:** the feature is switched off — the only channel with `allowsDeferredPayment` is inactive and
 * there are no tabs at all — so the screen says that and points at `/sales-channels`, rather than showing
 * an empty list that reads as *nobody owes anything*.
 */

type View = 'open' | 'all'

/**
 * What the screen draws instead of its money when the tabs read failed - FB-810, §9.530.
 *
 * The manager's own `data-load-failed` marker (`CmsPagesPage`'s, and §9.529's on the reports desk), so this
 * is the existing idiom rather than a third one. Local for now: no other `manager-kasa` screen shares the
 * shape, and the Tower's nineteen byte-identical copies are the argument for extracting at the first real
 * repetition rather than pre-emptively.
 */
function TabsLoadFailed({ retry }: { retry: () => void }) {
  return (
    <div
      className="rounded-xl border border-red-500/40 bg-red-500/5 px-4 py-3 text-xs space-y-2"
      data-load-failed
      data-tabs-failed
    >
      <p className="font-bold text-red-500 flex items-center gap-1.5">
        <AlertCircle className="w-3.5 h-3.5" />
        <T>Účty sa nepodarilo načítať</T>
      </p>
      <p className="text-muted-foreground">
        <T>Nezaplatená suma by tu bola nulová, nie skutočná - preto sa nezobrazuje.</T>
      </p>
      <button
        type="button"
        onClick={retry}
        data-tabs-retry
        className="px-2.5 py-1 rounded-lg border border-border font-semibold text-foreground"
      >
        <T>Skúsiť znova</T>
      </button>
    </div>
  )
}

export function OrderTabsPage() {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [view, setView] = useState<View>('open')
  const [settling, setSettling] = useState<{ tab: Tab; form: SettleForm } | null>(null)
  const [opening, setOpening] = useState<OpenForm | null>(null)
  const [adding, setAdding] = useState<Tab | null>(null)
  const [openedAtRead] = useState(() => Date.now())

  // One request for both views: `/order-tabs` is `/order-tabs/all` filtered to open, and the screen needs
  // the settled ones to show what a tab looks like once it is closed.
  const { data, isLoading, isError: tabsFailed } = useGetOrderTabsAll()
  const channels = useGetSalesChannels()

  const tabs = useMemo(() => oldestFirst(tabsOf(data)), [data])
  const shown = view === 'open' ? tabs.filter(isOpen) : tabs
  const totals = totalsOf(tabs.filter(isOpen))

  /**
   * Candidates for *put this on a tab*.
   *
   * `GET /orders` answers **nothing** without a date bound — measured — so a window is required rather
   * than optional. Thirty days is the span a tab plausibly reaches back over; anything older is a phone
   * call, not a till action.
   */
  const [candidateWindow] = useState(() => {
    const from = new Date()
    from.setDate(from.getDate() - 29)
    return { date_from: toDateParam(from), page_size: '200' }
  })
  const candidateQuery = useGetOrders(candidateWindow)
  const candidates = useMemo(() => candidatesOf(candidateQuery.data), [candidateQuery.data])
  const onSomeTab = useMemo(() => ordersOnAnyTab(tabs), [tabs])
  const allowsTabFor = (slug: string) => allowsTab(slug, filterChannelsOf(channels.data))

  const deferred = useMemo(
    () =>
      // The `as Record<string, unknown>` and the typeof guards under it were there because
      // /sales-channels answered an untyped object. It is `SalesChannel` now (BLOCKED #4), with
      // `name`, `isActive` and `allowsDeferredPayment` all guaranteed by their NOT NULL columns.
      (channels.data ?? [])
        .filter((channel) => channel.allowsDeferredPayment)
        .map((channel) => ({ name: channel.name, isActive: channel.isActive })),
    [channels.data],
  )
  /* FB-810, §9.530 - **withheld rather than invented.** `featureNote` is a *diagnosis*: it tells the
     operator why the list is empty and where to go. `filterChannelsOf(undefined)` is `[]`, so on a failed
     read it diagnoses from nothing and answers *"Žiadny predajný kanál nemá povolenú platbu neskôr, takže
     účet sa nedá otvoriť"* - which sends somebody to switch on a channel that is already on. Measured: both
     franchises carry `allows_deferred_payment` on two channels (§9.508), so it is false for them whenever
     this read fails.
     **Not a banner**, deliberately: a red notice about a channel list, on a screen whose tabs loaded fine,
     reports a fault in the wrong place. What is wrong is the explanation, so the explanation goes. */
  const offNote = channels.isError ? '' : featureNote(deferred)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: getGetOrderTabsAllQueryKey() })
    void queryClient.invalidateQueries({ queryKey: getGetOrderTabsQueryKey() })
  }

  const open = usePostOrderTabs({
    mutation: {
      onSuccess: () => {
        setOpening(null)
        refresh()
        toast({ title: tt('Účet otvorený') })
      },
      // §9.385 - the server's own sentence. `order_tabs` refuses by name: a channel that does not
      // allow a deferred account, a guest who already has one open.
      onError: (caught) => {
        setOpening(null)
        toast({
          variant: 'destructive',
          title: tt('Účet sa nepodarilo otvoriť'),
          description: apiErrorMessage(caught, tt('Server ho odmietol.')),
        })
      },
    },
  })

  const append = usePostOrderTabsIdOrdersOrderId({
    mutation: {
      onSuccess: () => {
        setAdding(null)
        refresh()
        toast({ title: tt('Objednávka pridaná na účet') })
      },
      // §9.385 - *pravdepodobne* was a guess. The server says which it is.
      onError: (caught) => {
        setAdding(null)
        toast({
          variant: 'destructive',
          title: tt('Objednávku sa nepodarilo pridať'),
          description: apiErrorMessage(
            caught,
            tt('Server ju odmietol - kanál pravdepodobne neumožňuje odloženú platbu.'),
          ),
        })
      },
    },
  })

  const settle = usePostOrderTabsIdSettle({
    mutation: {
      onSuccess: () => {
        setSettling(null)
        refresh()
        /* §9.521 - the money is in and no doklad is. Saying so beats a bare success on a screen where
           somebody has just taken cash for four orders and is waiting for paper. */
        toast({ title: tt('Úhrada zaznamenaná'), description: noDocumentYetNote() })
      },
      // §9.385 - money: the amount the server refused and why is what the till needs to read.
      onError: (caught) => {
        setSettling(null)
        toast({
          variant: 'destructive',
          title: tt('Úhrada sa nezaznamenala'),
          description: apiErrorMessage(
            caught,
            tt('Server ju odmietol - skontrolujte sumu a zostatok účtu.'),
          ),
        })
      },
    },
  })

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Účty na sekeru')}
        description={tt('Objednávky, ktoré ostali nezaplatené, a čo sa s nimi stane pri úhrade.')}
        icon={<Receipt className="w-5 h-5" />}
        actions={
          <button
            type="button"
            data-open-tab
            onClick={() => setOpening(openFormFor())}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50"
          >
            <Plus className="w-3.5 h-3.5" />
            <T>Otvoriť účet</T>
          </button>
        }
      />

      {/* FB-182 - the channel is off, which is the only reason this list is empty. */}
      {offNote !== '' && (
        <p className="flex items-start gap-1.5 text-xs text-amber-500" data-feature-off>
          <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
          <span>{offNote}</span>
        </p>
      )}

      {/* FB-810, §9.530 - **the colour is the claim.** `Nezaplatené` is amber above zero and **emerald at
          zero**, so a failed read renders `0,00 €` in the green this screen uses for *settled* - a fault
          styled as reassurance. `outstandingNote` also returns `''` at zero tabs, so the sentence explaining
          that an open tab is not missing cash disappears with it: the zero, its colour and its caveat all
          fail in the same direction. Replaced rather than annotated, for §9.529's reason. */}
      {tabsFailed ? (
        <TabsLoadFailed retry={refresh} />
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-tab-totals>
          <StatCard
            label={tt('Otvorených účtov')}
            value={formatCount(totals.tabs)}
            icon={Receipt}
            color="amber"
          />
          <StatCard
            label={tt('Nezaplatené')}
            value={formatCents(totals.outstandingCents)}
            icon={Wallet}
            color={totals.outstandingCents > 0 ? 'amber' : 'emerald'}
          />
          <StatCard
            label={tt('Už uhradené')}
            value={formatCents(totals.settledCents)}
            icon={Banknote}
            color="emerald"
          />
          <StatCard
            label={tt('Objednané spolu')}
            value={formatCents(totals.orderedCents)}
            icon={Receipt}
            color="blue"
          />
        </div>
      )}

      {/* §9.5: the outstanding balance is visible where cash expectations show - and says what it is not. */}
      {outstandingNote(totals) !== '' && (
        <p className="text-xs text-muted-foreground" data-outstanding-note>
          {outstandingNote(totals)}
        </p>
      )}

      <TabGroup
        tabs={[
          { id: 'open', label: tt('Otvorené') },
          { id: 'all', label: tt('Všetky') },
        ]}
        active={view}
        onChange={(id) => setView(id as View)}
      />

      {/* Gated separately from the cards above, because the two are different claims: *no open tab* is about
          the list and the green zero is about the money. Suppressing one would leave the other saying it. */}
      {tabsFailed ? null : shown.length === 0 ? (
        <EmptyState
          icon={<Receipt className="w-8 h-8" />}
          title={view === 'open' ? tt('Žiadny otvorený účet') : tt('Zatiaľ žiadne účty')}
          description={
            offNote !== ''
              ? offNote
              : tt(
                  'Účet sa otvorí na meno alebo na zákazníka a objednávky sa naň pridávajú z pokladne.',
                )
          }
        />
      ) : (
        <div className="space-y-3" data-tabs={shown.length}>
          {shown.map((tab) => {
            const days = daysOpen(tab, openedAtRead)
            return (
              <div
                key={tab.id}
                className={cn(
                  'rounded-2xl border p-4 space-y-3',
                  isOpen(tab) ? 'border-amber-500/40 bg-card' : 'border-border bg-muted/30',
                )}
                data-tab={tab.id}
                data-tab-status={tab.status}
              >
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-sm flex items-center gap-2">
                      {tab.name}
                      {isOpen(tab) ? (
                        <StatusBadge
                          label={tt('otvorený')}
                          color={isStale(days) ? 'red' : 'amber'}
                        />
                      ) : (
                        <StatusBadge label={tt('uzavretý')} color="emerald" />
                      )}
                    </p>
                    <p
                      className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5"
                      data-age
                    >
                      <Clock className="w-3 h-3" />
                      {tab.openedAt === null ? '-' : formatDateTime(tab.openedAt)} ·{' '}
                      {ageLabel(days)}
                      {isStale(days) && ` · ${tt('dlho otvorený')}`}
                    </p>
                    {tab.note !== null && (
                      <p className="text-[11px] text-muted-foreground italic mt-0.5">{tab.note}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[11px] text-muted-foreground uppercase tracking-wider">
                      <T>Nezaplatené</T>
                    </p>
                    <p className="font-bold tabular-nums" data-outstanding>
                      {formatCents(tab.outstandingCents)}
                    </p>
                    <p className="text-[11px] text-muted-foreground tabular-nums">
                      {formatCents(tab.settledCents)} / {formatCents(tab.orderedCents)}
                    </p>
                  </div>
                </div>

                {tab.orders.length > 0 && (
                  <div
                    className="text-xs text-muted-foreground"
                    data-tab-orders={tab.orders.length}
                  >
                    {tab.orders
                      .map((order) => `#${order.orderNumber} ${formatCents(order.totalCents)}`)
                      .join(' · ')}
                  </div>
                )}

                {tab.settlements.length > 0 && (
                  <div className="space-y-1" data-settlements={tab.settlements.length}>
                    {tab.settlements.map((settlement) => (
                      <p key={settlement.id} className="text-[11px] text-muted-foreground">
                        {settlement.settledAt === null ? '-' : formatDateTime(settlement.settledAt)}{' '}
                        · {formatCents(settlement.amountCents)} ·{' '}
                        {paymentLabel(settlement.paymentMethod)}
                      </p>
                    ))}
                    {/* §9.521 - beside the settlements rather than only in the toast, because the toast is
                     * gone by the time anybody wonders where the doklad is. Deleted with its helper when
                     * §10A.259's print lands. */}
                    <p className="text-[11px] text-amber-500" data-no-document-yet>
                      {noDocumentYetNote()}
                    </p>
                  </div>
                )}

                {isOpen(tab) && (
                  <button
                    type="button"
                    data-add-order={tab.id}
                    onClick={() => setAdding(tab)}
                    className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold mr-2"
                  >
                    <T>Pridať objednávku</T>
                  </button>
                )}

                {isOpen(tab) && (
                  <button
                    type="button"
                    data-settle={tab.id}
                    onClick={() => setSettling({ tab, form: settleFormFor(tab) })}
                    className={cn('px-3 py-1.5 rounded-lg', BUTTON_PRIMARY, 'text-xs')}
                  >
                    <T>Prijať úhradu</T>
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}

      {settling !== null && (
        <SettleDialog
          tab={settling.tab}
          form={settling.form}
          saving={settle.isPending}
          onChange={(form) => setSettling({ ...settling, form })}
          onClose={() => setSettling(null)}
          onConfirm={() =>
            settle.mutate({ id: settling.tab.id, data: settlePayload(settling.form) })
          }
        />
      )}

      {adding !== null && (
        <AddOrderDialog
          tab={adding}
          candidates={eligibleForTab(candidates, adding, allowsTabFor, onSomeTab)}
          emptyNote={noCandidatesNote(candidates, allowsTabFor, onSomeTab)}
          saving={append.isPending}
          onClose={() => setAdding(null)}
          onPick={(orderId) => append.mutate({ id: adding.id, orderId })}
        />
      )}

      {opening !== null && (
        <OpenTabDialog
          form={opening}
          saving={open.isPending}
          onChange={setOpening}
          onClose={() => setOpening(null)}
          onConfirm={() => open.mutate({ data: openPayload(opening) })}
        />
      )}
    </div>
  )
}

/**
 * Opening a tab — a form, because the server needs a name.
 *
 * <p>This was a `ConfirmDialog` until 2026-08-11: a title, a description telling the user the tab
 * *"needs a name somebody will recognise later"*, and two buttons — with nowhere to type one. It sent
 * `name: ''` every time, which `OrderTabService.create` reads as `null` and refuses with a 400, so
 * **no tab could ever be opened**. Everything else on this screen hangs off a tab, so the whole page
 * read as dead.
 */
function OpenTabDialog({
  form,
  saving,
  onChange,
  onClose,
  onConfirm,
}: {
  form: OpenForm
  saving: boolean
  onChange: (form: OpenForm) => void
  onClose: () => void
  onConfirm: () => void
}) {
  const problems = openTabProblems(form)

  /* §9.489, `TODO.md` 3f - it said `aria-modal="true"` and confined nothing. Ranked first of the 29 because a
     tab is money owed: this screen opens one, names it, takes payment for it and appends orders to it.
     Guarded on `saving`, which is what its own confirm button is disabled by - §9.480's rule that an overlay
     mid-write may not be dismissed by Escape. The wrapper is the scrim (it carries `onClick={onClose}`), so
     the role stays on it and the trap is bounded by the card inside. */
  const panelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, saving ? null : onClose)

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={tt('Otvoriť účet')}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl w-full max-w-md overflow-hidden flex flex-col shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        ref={panelRef}
        onKeyDown={onKeyDown}
        data-open-tab-dialog
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-bold text-sm">
            <T>Otvoriť účet</T>
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('Zatvoriť')}
            data-dialog-cancel
            className="p-1.5 rounded-lg hover:bg-muted"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-xs text-muted-foreground">
            <T>
              Objednávky sa naň pridávajú z pokladne a nezaregistrujú sa, kým sa účet nezaplatí.
            </T>
          </p>

          <label className="block space-y-1.5">
            <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium block">
              <T>Meno účtu</T>
            </span>
            <input
              value={form.name}
              onChange={(event) => onChange({ ...form, name: event.target.value })}
              aria-label={tt('Meno účtu')}
              data-open-tab-name
              autoFocus
              placeholder={tt('napr. Stôl 4 - Novák')}
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20"
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium block">
              <T>Poznámka</T>
            </span>
            <input
              value={form.note}
              onChange={(event) => onChange({ ...form, note: event.target.value })}
              aria-label={tt('Poznámka')}
              data-open-tab-note
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20"
            />
          </label>

          {problems.map((problem) => (
            <p key={problem} data-open-tab-problem={problem} className="text-xs text-destructive">
              {openProblemMessage(problem)}
            </p>
          ))}
        </div>

        <div className="flex gap-2 px-5 py-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2 rounded-lg border border-border text-xs font-semibold"
          >
            <T>Zrušiť</T>
          </button>
          <button
            type="button"
            data-open-tab-confirm
            disabled={saving || !canOpen(form)}
            onClick={onConfirm}
            className={cn('flex-1 py-2 rounded-lg text-xs', BUTTON_PRIMARY, 'disabled:opacity-50')}
          >
            {saving ? <Spinner className="w-4 h-4 mx-auto" /> : <T>Otvoriť</T>}
          </button>
        </div>
      </div>
    </div>
  )
}

function SettleDialog({
  tab,
  form,
  saving,
  onChange,
  onClose,
  onConfirm,
}: {
  tab: Tab
  form: SettleForm
  saving: boolean
  onChange: (form: SettleForm) => void
  onClose: () => void
  onConfirm: () => void
}) {
  const problems = settleProblems(form, tab)

  /* §9.489 - its own pair, because each of this file's three dialogs is a separate component. This is the one
     that takes the money, so the Escape guard on `saving` matters most here. */
  const panelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, saving ? null : onClose)

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={tt('Prijať úhradu')}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl w-full max-w-md overflow-hidden flex flex-col shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        ref={panelRef}
        onKeyDown={onKeyDown}
        data-settle-dialog
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-bold text-sm">
            {tt('Úhrada')} - {tab.name}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('Zatvoriť')}
            data-dialog-cancel
            className="p-1.5 rounded-lg hover:bg-muted"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-xs text-muted-foreground">
            <T>Zostatok</T>:{' '}
            <strong className="text-foreground tabular-nums">
              {formatCents(tab.outstandingCents)}
            </strong>
          </p>

          <label className="block space-y-1.5">
            <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium block">
              <T>Suma</T>
            </span>
            <input
              value={form.amount}
              onChange={(event) => onChange({ ...form, amount: event.target.value })}
              aria-label={tt('Suma')}
              data-settle-amount
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm tabular-nums outline-none focus:ring-2 focus:ring-primary/20"
            />
          </label>

          <div className="grid grid-cols-2 gap-1.5" data-settle-methods>
            {PAYMENT_METHODS.map((method) => (
              <button
                key={method}
                type="button"
                data-method={method}
                data-method-active={form.paymentMethod === method ? 'yes' : 'no'}
                onClick={() => onChange({ ...form, paymentMethod: method })}
                className={cn(
                  'py-2 rounded-lg border text-xs font-semibold transition-colors',
                  form.paymentMethod === method
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border text-muted-foreground hover:text-foreground',
                )}
              >
                {paymentLabel(method)}
              </button>
            ))}
          </div>

          <label className="block space-y-1.5">
            <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium block">
              <T>Poznámka</T>
            </span>
            <input
              value={form.note}
              onChange={(event) => onChange({ ...form, note: event.target.value })}
              aria-label={tt('Poznámka')}
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20"
            />
          </label>

          {problems.length > 0 ? (
            <div className="space-y-1" data-settle-problems>
              {problems.map((problem) => (
                <p key={problem} className="text-xs text-red-500 flex items-start gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
                  {settleProblemMessage(problem, tab)}
                </p>
              ))}
            </div>
          ) : (
            // What this payment does, before it is taken: whether it closes the tab, and what gets
            // fiscalised — which is the part nobody expects.
            <p
              className={cn(
                'text-xs',
                wouldClose(form, tab) ? 'text-emerald-500' : 'text-muted-foreground',
              )}
              data-settle-summary
            >
              {settleSummary(form, tab)}
            </p>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
          >
            <T>Zrušiť</T>
          </button>
          <button
            type="button"
            data-settle-confirm
            onClick={onConfirm}
            disabled={saving || !canSettle(form, tab)}
            className={cn('flex-1 px-4 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            {saving ? <Spinner className="w-4 h-4 mx-auto" /> : <T>Prijať úhradu</T>}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * *Put this order on the tab.*
 *
 * The list is already narrowed to what `append` would accept, so a refusal is not the way the operator
 * finds out what the rules are. When it is empty it says **why** — a channel with no deferred payment and
 * an order already on another tab are different problems.
 */
function AddOrderDialog({
  tab,
  candidates,
  emptyNote,
  saving,
  onClose,
  onPick,
}: {
  tab: Tab
  candidates: CandidateOrder[]
  emptyNote: string
  saving: boolean
  onClose: () => void
  onPick: (orderId: number) => void
}) {
  /* §9.489 - this one holds no form, so the opening focus falls to the marked cancel rather than to the first
     order in the list, which would put a reflexive Enter on *append this order to the tab*. */
  const panelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, saving ? null : onClose)

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={tt('Pridať objednávku na účet')}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        ref={panelRef}
        onKeyDown={onKeyDown}
        data-add-dialog
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-bold text-sm">
            {tt('Pridať na účet')} - {tab.name}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('Zatvoriť')}
            data-dialog-cancel
            className="p-1.5 rounded-lg hover:bg-muted"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {candidates.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-add-empty>
              {emptyNote}
            </p>
          ) : (
            <div className="divide-y divide-border" data-add-candidates={candidates.length}>
              {candidates.map((order) => (
                <button
                  key={order.id}
                  type="button"
                  data-add-candidate={order.id}
                  disabled={saving}
                  onClick={() => onPick(order.id)}
                  className="w-full flex items-center gap-3 py-2.5 text-left disabled:opacity-50"
                >
                  <span className="font-mono text-xs font-bold text-primary">
                    #{order.orderNumber}
                  </span>
                  <span className="flex-1 truncate text-sm">
                    {order.customerName ?? tt('bez mena')}
                  </span>
                  <span className="font-bold tabular-nums text-sm">
                    {formatCents(order.totalCents)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
